export interface SoftlockPasteMismatchEvent {
  type: 'paste_mismatch';
  source: string;
  timestamp: number;
  text: string;
  xPath?: string | null;
  targetType?: string | null;
  copiedCacheSize?: number;
  subSessionId?: number | null;
  subSessionSlug?: string | null;
  subSessionCaption?: string | null;
  questionId?: number | null;
  questionCaption?: string | null;
};

interface CopiedTextEvent {
  type: 'copy' | 'cut';
  source: string;
  timestamp: number;
  text: string;
  xPath?: string | null;
};

export class PasteMismatchDetector {
  // this is intentionally smaller than io aggregators on other clients
  // it only answers whether pasted text came from a recent copy inside this assessment page
  private clipboardTextEvents: CopiedTextEvent[] = [];
  private eventListeners: { type: string; listener: EventListenerOrEventListenerObject; options?: boolean | AddEventListenerOptions }[] = [];
  private readonly clipboardTextCacheLimit = 3;

  constructor(
    private onPasteMismatch: (event: SoftlockPasteMismatchEvent) => void
  ) {}

  public start(): void {
    // only starts the narrow softlock paste check, not the full process log io loop
    this.addEventListeners();
  }

  public destroy(): void {
    this.eventListeners.forEach(({ type, listener, options }) => {
      document.removeEventListener(type, listener, options);
    });
    this.eventListeners = [];
    this.clipboardTextEvents = [];
  }

  private addEventListeners(): void {
    // keep copy and cut text briefly so a later paste can be compared against it
    this.addListener('copy', (e: ClipboardEvent) => {
      this.trackCopiedText(e, 'COPY_EVENT', 'copy');
    });

    this.addListener('cut', (e: ClipboardEvent) => {
      this.trackCopiedText(e, 'CUT_EVENT', 'cut');
    });

    this.addListener('paste', (e: ClipboardEvent) => {
      const timestamp = Date.now();
      // only response inputs are relevant for softlock paste mismatch
      const targetInfo = this.resolveTextResponseTarget(e.target);
      if (!targetInfo) {
        return;
      }
      const pasteText = e.clipboardData?.getData('text/plain') || '';
      if (!this.normalizeTextForPasteMatch(pasteText).length) {
        return;
      }
      // if the pasted text was not copied or cut from this page recently, it is treated as a mismatch
      const matchedCopyText = this.findMatchingCopiedText(pasteText);
      if (matchedCopyText != null) {
        return;
      }
      // student softlock service decides whether this should pause the student
      this.onPasteMismatch({
        type: 'paste_mismatch',
        source: 'PASTE_EVENT_MISMATCH',
        timestamp,
        text: pasteText,
        xPath: targetInfo.xPath,
        targetType: targetInfo.targetType,
        copiedCacheSize: this.clipboardTextEvents.length,
      });
    }, true);
  }

  private addListener(eventType: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void {
    document.addEventListener(eventType, listener, options);
    this.eventListeners.push({ type: eventType, listener, options });
  }

  private trackCopiedText(e: ClipboardEvent, source: 'COPY_EVENT' | 'CUT_EVENT', type: 'copy' | 'cut'): void {
    const copiedText = this.extractClipboardText(e);
    if (!copiedText.length) {
      return;
    }
    const target = this.getElementFromEventTarget(e.target);
    this.clipboardTextEvents.push({
      type,
      source,
      timestamp: Date.now(),
      text: copiedText,
      xPath: this.getXPath(target),
    });
    // keep the cache small so we only trust recent in-assessment copied text
    if (this.clipboardTextEvents.length > this.clipboardTextCacheLimit) {
      this.clipboardTextEvents.splice(0, this.clipboardTextEvents.length - this.clipboardTextCacheLimit);
    }
  }

  private extractClipboardText(e: ClipboardEvent): string {
    const clipboardText = e.clipboardData?.getData('text/plain');
    if (clipboardText && clipboardText.length > 0) {
      return clipboardText;
    }
    // some browsers do not expose clipboard text on copy, so use the selected input text
    const target = e.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
      const selectionStart = target.selectionStart ?? 0;
      const selectionEnd = target.selectionEnd ?? 0;
      if (selectionEnd > selectionStart) {
        return (target.value || '').slice(selectionStart, selectionEnd);
      }
    }
    return window.getSelection()?.toString() || '';
  }

  private normalizeTextForPasteMatch(text: string): string {
    if (typeof text !== 'string' || !text.length) {
      return '';
    }
    // newline formats can differ across devices and browsers
    return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  }

  private findMatchingCopiedText(pasteText: string): string | null {
    const normalizedPasteText = this.normalizeTextForPasteMatch(pasteText);
    if (!normalizedPasteText.length) {
      return null;
    }
    // only compare against the small in-page cache, never against the system clipboard history
    for (const copiedEvent of this.clipboardTextEvents) {
      const copiedText = copiedEvent.text || '';
      const normalizedCopiedText = this.normalizeTextForPasteMatch(copiedText);
      if (!normalizedCopiedText.length) {
        continue;
      }
      if (this.hasTextOverlap(normalizedCopiedText, normalizedPasteText)) {
        return copiedText;
      }
    }
    return null;
  }

  private hasTextOverlap(a: string, b: string): boolean {
    if (!a.length || !b.length) {
      return false;
    }
    // partial overlap handles pasting a subsection of text copied from the assessment
    return a.includes(b) || b.includes(a);
  }

  private resolveTextResponseTarget(target: EventTarget | null): { xPath: string | null; targetType: string } | null {
    const targetElement = this.getElementFromEventTarget(target);
    if (!targetElement) {
      return null;
    }
    if (targetElement.closest('capture-math')) {
      return null;
    }
    // keep this scoped to typed response areas so normal page paste actions are ignored
    // notepad is included because students can draft response text there
    const isInputResponse = !!targetElement.closest('element-render-input');
    const isScientificNotationResponse = !!targetElement.closest('element-render-scientific-notation');
    const isNotepad = !!targetElement.closest('.notepad-container');
    if (!isInputResponse && !isScientificNotationResponse && !isNotepad) {
      return null;
    }
    let targetType = 'RESPONSE_CONTAINER';
    if (isScientificNotationResponse) {
      targetType = 'SCIENTIFIC_NOTATION_INPUT';
    } else if (isNotepad) {
      targetType = 'NOTEPAD';
    } else if (targetElement.closest('.ck-editor') || targetElement.closest('.ck-content')) {
      targetType = 'CKEDITOR';
    } else if (targetElement instanceof HTMLTextAreaElement) {
      targetType = 'TEXTAREA';
    } else if (targetElement instanceof HTMLInputElement) {
      targetType = 'INPUT';
    }
    return {
      xPath: this.getXPath(targetElement),
      targetType,
    };
  }

  private getElementFromEventTarget(target: EventTarget | null): Element | null {
    if (!target) {
      return null;
    }
    if (target instanceof Element) {
      return target;
    }
    if (target instanceof Node) {
      return target.parentElement;
    }
    return null;
  }

  private getXPath(element: Element | null): string | null {
    if (!element) {
      return null;
    }
    if (element.id !== '') {
      return `id("${element.id}")`;
    }
    if (element === document.body) {
      return '/html/body';
    }

    let ix = 0;
    const siblings = element.parentNode?.childNodes;
    if (!siblings) return null;

    for (let i = 0; i < siblings.length; i++) {
      const sibling = siblings[i] as Element;
      if (sibling === element) {
        return `${this.getXPath(element.parentNode as Element)}/${element.tagName.toLowerCase()}[${ix + 1}]`;
      }
      if (sibling.nodeType === 1 && sibling.tagName === element.tagName) {
        ix++;
      }
    }
    return null;
  }
}
